import React, { useEffect } from "react";
import {
  Mail, Phone, MapPin, ArrowUpRight, ShieldCheck,
  GraduationCap, FileCheck, ClipboardList, CalendarDays,
  BookOpen, Award, Target, Eye, Lightbulb,
  CheckCircle2, Clock, FileText, Users, Sparkles,
  ExternalLink, ChevronRight
} from "lucide-react";
import { motion } from "framer-motion";
import LogoFallback from "/assets/img/bubbanlogo.jpg";

const fadeUp = {
  hidden: { opacity: 0, y: 35 },
  visible: {
    opacity: 1,
    y: 0,
    transition: { duration: 0.7, ease: "easeOut" }
  }
};

const services = [
  {
    icon: CalendarDays,
    title: "Examination Scheduling",
    description: "Planning and coordinating examination schedules and academic calendars.",
    number: "01"
  },
  {
    icon: ClipboardList,
    title: "Examination Management",
    description: "Coordinating examinations and ensuring smooth examination operations.",
    number: "02"
  },
  {
    icon: FileCheck,
    title: "Results Processing",
    description: "Managing examination results, verification and publication.",
    number: "03"
  },
  {
    icon: GraduationCap,
    title: "Academic Records",
    description: "Maintaining examination records and academic documentation.",
    number: "04"
  },
  {
    icon: Award,
    title: "Certificates & Transcripts",
    description: "Facilitating academic certificates, transcripts and related services.",
    number: "05"
  },
  {
    icon: ShieldCheck,
    title: "Academic Integrity",
    description: "Supporting transparency, confidentiality and examination standards.",
    number: "06"
  }
];

const principles = [
  {
    icon: Eye,
    title: "Our Vision",
    color: "bg-blue-600",
    description: "To become a leading institution of higher learning in Asia."
  },
  {
    icon: Target,
    title: "Our Mission",
    color: "bg-emerald-600",
    description: "To nurture creativity, research, and leadership through quality education."
  },
  {
    icon: Lightbulb,
    title: "Our Goals",
    color: "bg-orange-500",
    description: "Expanding research opportunities and strengthening global partnerships."
  }
];

export default function Coe({
  logoSrc = LogoFallback,
  accent = "#115e32",
  pageTitle = "Controller of Examinations | Bandarban University"
}) {
  useEffect(() => {
    document.title = pageTitle;
  }, [pageTitle]);

  const photoUrl =
    `${import.meta.env.VITE_API_SOURCE_URL}/assets/images/jahid.jpg`;

  return (
    <div className="bg-[#f6f9f7] text-gray-800 overflow-hidden">

      {/* HERO SECTION */}
      <section className="relative min-h-[470px] bg-[#073b2a] text-white overflow-hidden flex items-center">

        <div
          className="absolute inset-0 opacity-20"
          style={{
            backgroundImage: "radial-gradient(#a7f3d0 1px, transparent 1px)",
            backgroundSize: "28px 28px"
          }}
        />

        <motion.div
          animate={{ rotate: 360 }}
          transition={{ duration: 50, repeat: Infinity, ease: "linear" }}
          className="absolute -right-36 -top-48 w-[600px] h-[600px] rounded-full border border-white/10"
        />

        <div className="absolute right-0 bottom-0 w-96 h-96 bg-emerald-400/20 blur-[120px] rounded-full" />

        <div className="relative max-w-7xl mx-auto px-6 py-24 w-full grid lg:grid-cols-2 gap-12 items-center">

          <motion.div
            initial="hidden"
            animate="visible"
            variants={fadeUp}
          >
            <div className="inline-flex items-center gap-2 px-4 py-2 rounded-full bg-white/10 border border-white/20 text-emerald-200 text-sm mb-7">
              <Sparkles size={16} />
              Bandarban University
            </div>

            <h1 className="text-4xl md:text-6xl font-extrabold leading-tight">
              Office of the
              <span className="block text-emerald-300">
                Controller of Examinations
              </span>
            </h1>

            <p className="text-gray-300 text-lg leading-8 mt-6 max-w-xl">
              Ensuring academic excellence through transparent,
              efficient and reliable examination management.
              Upholding integrity and maintaining the highest
              standards of academic administration.
            </p>

            <div className="flex flex-wrap gap-4 mt-9">
              <a
                href="#controller"
                className="px-7 py-3.5 rounded-xl bg-emerald-400 text-[#073b2a] font-bold hover:bg-white transition flex items-center gap-2"
              >
                Meet the Controller
                <ArrowUpRight size={19} />
              </a>

              <a
                href="#services"
                className="px-7 py-3.5 rounded-xl border border-white/30 hover:bg-white/10 transition"
              >
                Our Services
              </a>
            </div>
          </motion.div>

          {/* ANIMATED EXAMINATION GRAPHIC */}
          <motion.div
            initial={{ opacity: 0, x: 60 }}
            animate={{ opacity: 1, x: 0 }}
            transition={{ duration: 1 }}
            className="hidden lg:flex justify-center"
          >
            <div className="relative w-[360px] h-[360px] flex items-center justify-center">

              <motion.div
                animate={{ rotate: 360 }}
                transition={{ duration: 30, repeat: Infinity, ease: "linear" }}
                className="absolute inset-0 rounded-full border border-dashed border-emerald-300/40"
              />

              <div className="absolute inset-8 rounded-full border border-white/10" />

              <motion.div
                animate={{ y: [0, -12, 0] }}
                transition={{ duration: 4, repeat: Infinity }}
                className="relative w-64 h-64 rounded-[40px] bg-gradient-to-br from-emerald-300 to-teal-700 shadow-2xl flex flex-col items-center justify-center"
              >
                <FileCheck size={90} className="text-white mb-4" />
                <h3 className="text-2xl font-bold">Excellence</h3>
                <p className="text-sm text-white/80 mt-1">
                  Academic Integrity
                </p>
              </motion.div>

              {[
                { Icon: GraduationCap, pos: "top-2 left-8" },
                { Icon: BookOpen, pos: "top-8 right-4" },
                { Icon: Award, pos: "bottom-4 left-8" },
                { Icon: ClipboardList, pos: "bottom-5 right-5" }
              ].map(({ Icon, pos }, i) => (
                <motion.div
                  key={i}
                  animate={{ y: [0, -10, 0] }}
                  transition={{
                    duration: 3,
                    repeat: Infinity,
                    delay: i * 0.4
                  }}
                  className={`absolute ${pos} w-14 h-14 rounded-2xl bg-white text-emerald-800 shadow-xl flex items-center justify-center`}
                >
                  <Icon size={25} />
                </motion.div>
              ))}
            </div>
          </motion.div>
        </div>
      </section>

      {/* HIGHLIGHT STRIP */}
      <div className="relative z-10 max-w-6xl mx-auto px-6 -mt-8">
        <div className="bg-white rounded-2xl shadow-xl border border-gray-100 grid sm:grid-cols-3 divide-y sm:divide-y-0 sm:divide-x divide-gray-100">
          {[
            {
              icon: ShieldCheck,
              title: "Academic Integrity",
              text: "Transparent Examination Process"
            },
            {
              icon: Clock,
              title: "Efficient Management",
              text: "Organized Academic Operations"
            },
            {
              icon: FileCheck,
              title: "Reliable Records",
              text: "Accurate Academic Documentation"
            }
          ].map((item, i) => (
            <div key={i} className="p-6 flex items-center gap-4">
              <div className="w-12 h-12 shrink-0 rounded-xl bg-emerald-50 text-emerald-700 flex items-center justify-center">
                <item.icon size={24} />
              </div>
              <div>
                <h3 className="font-bold">{item.title}</h3>
                <p className="text-sm text-gray-500">{item.text}</p>
              </div>
            </div>
          ))}
        </div>
      </div>

      {/* CONTROLLER PROFILE */}
      <section id="controller" className="py-24 px-6">
        <div className="max-w-7xl mx-auto">

          <motion.div
            initial="hidden"
            whileInView="visible"
            viewport={{ once: true }}
            variants={fadeUp}
            className="text-center mb-14"
          >
            <span className="text-emerald-700 font-bold uppercase tracking-[3px] text-sm">
              Leadership & Administration
            </span>

            <h2 className="text-3xl md:text-4xl font-extrabold mt-3 text-gray-900">
              Meet the Controller
            </h2>

            <p className="text-gray-500 mt-4 max-w-2xl mx-auto">
              Dedicated leadership committed to excellence,
              transparency and academic integrity.
            </p>
          </motion.div>

          <motion.div
            initial={{ opacity: 0, y: 45 }}
            whileInView={{ opacity: 1, y: 0 }}
            viewport={{ once: true }}
            transition={{ duration: 0.8 }}
            className="grid lg:grid-cols-5 bg-white rounded-[30px] shadow-xl overflow-hidden border border-gray-100"
          >

            {/* PROFILE IMAGE */}
            <div className="lg:col-span-2 relative min-h-[420px] bg-gradient-to-br from-emerald-900 to-teal-700">

              <div
                className="absolute inset-0 opacity-20"
                style={{
                  backgroundImage: "radial-gradient(white 1px, transparent 1px)",
                  backgroundSize: "20px 20px"
                }}
              />

              <img
                src={photoUrl}
                alt="Md. Jahidul Islam"
                className="relative w-full h-full min-h-[420px] object-cover object-top"
              />

              <div className="absolute bottom-5 left-5 right-5 p-5 rounded-2xl bg-white/90 backdrop-blur-md">
                <p className="font-bold text-gray-900 text-lg">
                  Md. Jahidul Islam
                </p>
                <p className="text-sm text-emerald-800 font-semibold">
                  Exam Controller (In Charge)
                </p>
              </div>
            </div>

            {/* PROFILE INFORMATION */}
            <div className="lg:col-span-3 p-7 md:p-12">

              <span className="inline-flex items-center gap-2 text-emerald-700 text-sm font-bold uppercase tracking-wider">
                <GraduationCap size={19} />
                Office Leadership
              </span>

              <h2 className="text-3xl md:text-4xl font-extrabold text-gray-900 mt-5">
                Md. Jahidul Islam
              </h2>

              <p className="text-emerald-700 font-semibold mt-3 text-lg">
                Exam Controller (In Charge)
              </p>

              <p className="text-gray-500 font-medium mt-1">
                Bandarban University
              </p>

              <div className="h-1 w-20 bg-emerald-500 rounded-full mt-7" />

              <p className="text-gray-600 leading-8 mt-7">
                The Office of the Controller of Examinations plays
                an essential role in maintaining academic standards
                and ensuring the effective administration of
                university examinations. Under dedicated leadership,
                the office strives to provide reliable, transparent
                and student-focused examination services.
              </p>

              <div className="grid sm:grid-cols-2 gap-4 mt-8">

                <a
                  href="tel:+8801812601277"
                  className="flex items-center gap-3 p-4 rounded-xl bg-gray-50 hover:bg-emerald-50 transition"
                >
                  <Phone className="text-emerald-700 shrink-0" />
                  <div>
                    <p className="text-xs text-gray-500">Contact Number</p>
                    <p className="font-semibold text-sm">01812601277</p>
                  </div>
                </a>

                <a
                  href="mailto:jahidbu1990@gmail.com"
                  className="flex items-center gap-3 p-4 rounded-xl bg-gray-50 hover:bg-emerald-50 transition"
                >
                  <Mail className="text-emerald-700 shrink-0" />
                  <div className="min-w-0">
                    <p className="text-xs text-gray-500">Email Address</p>
                    <p className="font-semibold text-sm break-all">
                      jahidbu1990@gmail.com
                    </p>
                  </div>
                </a>

              </div>

            </div>
          </motion.div>
		  
		   {/* JUNIOR SECTION OFFICER PROFILE */}
			<motion.div
			  initial={{ opacity: 0, y: 45 }}
			  whileInView={{ opacity: 1, y: 0 }}
			  viewport={{ once: true }}
			  transition={{ duration: 0.8 }}
			  className="mt-10 grid lg:grid-cols-5 bg-white rounded-[30px] shadow-xl overflow-hidden border border-gray-100"
			>
			  {/* PROFILE IMAGE */}
			  <div className="lg:col-span-2 relative min-h-[420px] bg-gradient-to-br from-emerald-900 to-teal-700 overflow-hidden">

				<div
				  className="absolute inset-0 opacity-20"
				  style={{
					backgroundImage:
					  "radial-gradient(white 1px, transparent 1px)",
					backgroundSize: "20px 20px",
				  }}
				/>

				<motion.img
				  src={`${import.meta.env.VITE_API_SOURCE_URL}/assets/images/misbah.jpg`}
				  alt="Misbah Uddin"
				  initial={{ scale: 1.08 }}
				  whileInView={{ scale: 1 }}
				  viewport={{ once: true }}
				  transition={{ duration: 1.2 }}
				  className="relative w-full h-full min-h-[420px] object-cover object-top"
				/>

				<div className="absolute inset-0 bg-gradient-to-t from-emerald-950/80 via-transparent to-transparent" />

				<motion.div
				  initial={{ opacity: 0, y: 20 }}
				  whileInView={{ opacity: 1, y: 0 }}
				  viewport={{ once: true }}
				  transition={{ delay: 0.3 }}
				  className="absolute bottom-5 left-5 right-5 p-5 rounded-2xl bg-white/90 backdrop-blur-md shadow-lg"
				>
				  <p className="font-bold text-gray-900 text-lg">
					Misbah Uddin
				  </p>

				  <p className="text-sm text-emerald-800 font-semibold">
					Section Officer
				  </p>
				</motion.div>
			  </div>

			  {/* PROFILE INFORMATION */}
			  <div className="lg:col-span-3 p-7 md:p-12">

				<span className="inline-flex items-center gap-2 text-emerald-700 text-sm font-bold uppercase tracking-wider">
				  <GraduationCap size={19} />
				  Examination Administration
				</span>

				<h2 className="text-3xl md:text-4xl font-extrabold text-gray-900 mt-5">
				  Misbah Uddin
				</h2>

				<p className="text-emerald-700 font-semibold mt-3 text-lg">
					Section Officer
				</p>

				<p className="text-gray-500 font-medium mt-1">
				  Controller of Examination
				</p>

				<p className="text-gray-500 font-medium">
				  Bandarban University
				</p>

				<div className="h-1 w-20 bg-emerald-500 rounded-full mt-7" />

				<p className="text-gray-600 leading-8 mt-7">
				  Misbah Uddin serves as Section Officer
				  in the Controller of Examination department
				  of Bandarban University, supporting examination
				  administration and academic documentation.
				</p>

				{/* CONTACT INFORMATION */}
				<div className="grid sm:grid-cols-2 gap-4 mt-8">

				  {/* Phone */}
				  <a
					href="tel:+8801840006667"
					className="flex items-center gap-3 p-4 rounded-xl bg-gray-50 hover:bg-emerald-50 border border-gray-100 hover:border-emerald-200 transition duration-300 group"
				  >
					<div className="w-11 h-11 rounded-xl bg-emerald-100 text-emerald-700 flex items-center justify-center group-hover:bg-emerald-700 group-hover:text-white transition">
					  <Phone size={21} />
					</div>

					<div>
					  <p className="text-xs text-gray-500">
						Contact Number
					  </p>

					  <p className="font-semibold text-sm text-gray-800">
						01840006667
					  </p>
					</div>
				  </a>

				  {/* Email */}
				  <a
					href="mailto:misbah.com.bu@gmail.com"
					className="flex items-center gap-3 p-4 rounded-xl bg-gray-50 hover:bg-emerald-50 border border-gray-100 hover:border-emerald-200 transition duration-300 group"
				  >
					<div className="w-11 h-11 rounded-xl bg-emerald-100 text-emerald-700 flex items-center justify-center group-hover:bg-emerald-700 group-hover:text-white transition">
					  <Mail size={21} />
					</div>

					<div className="min-w-0">
					  <p className="text-xs text-gray-500">
						Email Address
					  </p>

					  <p className="font-semibold text-sm text-gray-800 break-all">
						misbah.com.bu@gmail.com
					  </p>
					</div>
				  </a>

				</div>

				{/* DEPARTMENT INFORMATION */}
				<div className="mt-8 p-5 rounded-2xl bg-emerald-50 border border-emerald-100">
				  <div className="flex items-center gap-3">

					<div className="w-10 h-10 rounded-xl bg-white text-emerald-700 flex items-center justify-center shadow-sm">
					  <FileCheck size={21} />
					</div>

					<div>
					  <p className="text-xs text-emerald-700 font-semibold uppercase tracking-wider">
						Department
					  </p>

					  <p className="font-bold text-gray-800 mt-1">
						Controller of Examination
					  </p>

					  <p className="text-sm text-gray-500">
						Bandarban University
					  </p>
					</div>

				  </div>
				</div>

			  </div>
			</motion.div>
		  
        </div>
      </section>
	
		

      {/* EXAMINATION SERVICES */}
      <section id="services" className="py-24 px-6 bg-white">

        <div className="max-w-7xl mx-auto">

          <div className="text-center mb-14">
            <span className="text-emerald-700 font-bold uppercase tracking-[3px] text-sm">
              What We Do
            </span>

            <h2 className="text-3xl md:text-4xl font-extrabold mt-3">
              Examination Services
            </h2>

            <p className="text-gray-500 mt-4 max-w-2xl mx-auto">
              Providing comprehensive examination and academic
              administrative services to support students and faculties.
            </p>
          </div>

          <div className="grid sm:grid-cols-2 lg:grid-cols-3 gap-6">

            {services.map((item, i) => (
              <motion.div
                key={item.number}
                initial={{ opacity: 0, y: 30 }}
                whileInView={{ opacity: 1, y: 0 }}
                viewport={{ once: true }}
                transition={{ delay: i * 0.08 }}
                whileHover={{ y: -8 }}
                className="group relative p-7 rounded-2xl border border-gray-100 bg-[#f8fbf9] hover:bg-[#0b4933] hover:text-white hover:shadow-2xl transition duration-300 overflow-hidden"
              >

                <span className="absolute top-5 right-6 text-5xl font-black text-gray-100 group-hover:text-white/10">
                  {item.number}
                </span>

                <div className="relative w-14 h-14 rounded-2xl bg-emerald-100 text-emerald-800 flex items-center justify-center group-hover:bg-white/20 group-hover:text-white transition">
                  <item.icon size={27} />
                </div>

                <h3 className="text-xl font-bold mt-6 relative">
                  {item.title}
                </h3>

                <p className="text-gray-500 group-hover:text-gray-200 mt-3 leading-7 text-sm relative">
                  {item.description}
                </p>

                <div className="mt-6 text-emerald-700 group-hover:text-emerald-300 flex items-center gap-2 text-sm font-bold">
                  Examination Services
                  <ChevronRight size={17} />
                </div>

              </motion.div>
            ))}

          </div>
        </div>
      </section>

      {/* VISION MISSION GOALS */}
      <section className="py-24 px-6 bg-[#f1f7f3]">

        <div className="max-w-7xl mx-auto">

          <div className="text-center mb-14">
            <span className="text-emerald-700 font-bold uppercase tracking-[3px] text-sm">
              Our Direction
            </span>

            <h2 className="text-3xl md:text-4xl font-extrabold mt-3">
              Vision, Mission & Goals
            </h2>
          </div>

          <div className="grid md:grid-cols-3 gap-7">

            {principles.map((item, i) => (
              <motion.div
                key={item.title}
                initial={{ opacity: 0, y: 30 }}
                whileInView={{ opacity: 1, y: 0 }}
                viewport={{ once: true }}
                transition={{ delay: i * 0.15 }}
                whileHover={{ y: -8 }}
                className="bg-white p-8 rounded-3xl shadow-md hover:shadow-xl transition"
              >
                <div className={`w-14 h-14 ${item.color} rounded-2xl text-white flex items-center justify-center`}>
                  <item.icon size={28} />
                </div>

                <h3 className="text-2xl font-bold mt-6">
                  {item.title}
                </h3>

                <p className="text-gray-500 leading-8 mt-4">
                  {item.description}
                </p>
              </motion.div>
            ))}

          </div>
        </div>
      </section>

      {/* CONTACT SECTION */}
      <section id="contact" className="relative overflow-hidden bg-[#073b2a] text-white py-20 px-6">

        <div className="absolute w-96 h-96 rounded-full bg-emerald-400/10 blur-3xl -right-20 -top-20" />

        <div className="relative max-w-7xl mx-auto">

          <div className="grid lg:grid-cols-2 gap-12 items-center">

            <div>
              <span className="text-emerald-300 font-bold uppercase tracking-[3px] text-sm">
                Get In Touch
              </span>

              <h2 className="text-3xl md:text-5xl font-extrabold mt-4 leading-tight">
                Your Academic Journey, Our Commitment
              </h2>

              <p className="text-gray-300 mt-5 leading-8">
                Contact the Office of the Controller of Examinations
                for examination-related information, academic records
                and administrative assistance.
              </p>

              <a
                href="mailto:coe@bubban.ac.bd"
                className="inline-flex items-center gap-3 mt-8 bg-emerald-400 text-[#073b2a] font-bold px-7 py-4 rounded-xl hover:bg-white transition"
              >
                Contact Examination Office
                <ArrowUpRight size={20} />
              </a>
            </div>

            <div className="bg-white/10 backdrop-blur-lg border border-white/10 rounded-3xl p-7 space-y-6">

              <div className="flex gap-4 items-start">
                <MapPin className="text-emerald-300 shrink-0" size={23} />
                <div>
                  <h4 className="font-bold">University Address</h4>
                  <p className="text-gray-300 text-sm mt-2 leading-7">
                    K.B Road, Shualok, Post Office: Shualok - 4600,
                    Bandarban Sadar, Bangladesh
                  </p>
                </div>
              </div>

              <div className="flex gap-4 items-center">
                <Phone className="text-emerald-300 shrink-0" />
                <div>
                  <h4 className="font-bold">Office Phone</h4>
                  <a href="tel:+8801318305900" className="text-gray-300 text-sm">
                    +880 1318-305900
                  </a>
                </div>
              </div>

              <div className="flex gap-4 items-center">
                <Mail className="text-emerald-300 shrink-0" />
                <div>
                  <h4 className="font-bold">Official Email</h4>
                  <a href="mailto:coe@bubban.ac.bd" className="text-gray-300 text-sm">
                    coe@bubban.ac.bd
                  </a>
                </div>
              </div>

              <div className="border-t border-white/20 pt-5">
                <p className="text-xs text-gray-400">
                  OFFICE OF THE CONTROLLER OF EXAMINATIONS
                </p>
                <p className="font-bold mt-1">
                  Bandarban University
                </p>
              </div>

            </div>
          </div>
        </div>
      </section>

    </div>
  );
}
